import { Button, Typography, useToast } from "@hellocarbo/lasagne";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  Form,
  LoaderFunctionArgs,
  useActionData,
  useBlocker,
  useLoaderData,
  useNavigation,
} from "react-router";
import invariant from "tiny-invariant";

import ScopesCard from "@/components/ExternalCampaign/ScopesExternalCampaign/ScopesCard";
import PageWrapper from "@/components/ui/PageWrapper";
import { TScope } from "@/types/scope";
import { extractBegesScopeNumber } from "@/utils/scope";

import ConfirmLeaveModal from "./ConfirmLeaveModal";
import { useExternalCampaignContext } from "./ExternalCampaign";

export async function loader({ params }: LoaderFunctionArgs) {
  const { externalCampaignId } = params;
  invariant(externalCampaignId, "No external campaign id found.");

  // Fetch scopes data to build the form
  const scopeResponse = await fetch("/api/client/scopes");
  if (scopeResponse.status !== 200) {
    throw data("Failed to load scopes", {
      status: scopeResponse.status,
    });
  }

  const { data: scopes } = (await scopeResponse.json()) as {
    data: Array<TScope>;
  };
  const begesScopes = extractBegesScopeNumber(scopes);

  // Fetch external campaign scopes carbon data to prefill the form with the scopes carbon data
  const externalCampaignBegesScopesCarbonResponse = await fetch(
    `/api/client/external_campaigns/${externalCampaignId}/beges_scopes_carbon`
  );
  if (externalCampaignBegesScopesCarbonResponse.status !== 200) {
    throw data("Failed to load externalCampaign", {
      status: externalCampaignBegesScopesCarbonResponse.status,
    });
  }

  const { data: externalCampaignBegesScopesCarbon } =
    (await externalCampaignBegesScopesCarbonResponse.json()) as {
      data: Record<string, number>;
    };

  return {
    begesScopes,
    externalCampaignBegesScopesCarbon,
  };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { externalCampaignId } = params;
  const formData = await request.formData();

  const requestData = formData
    .entries()
    .reduce((acc: Record<string, number>, scopeElem) => {
      acc[scopeElem[0]] = Number(scopeElem[1]);

      return acc;
    }, {});

  const response = await fetch(
    `/api/client/external_campaigns/${externalCampaignId}/beges_scopes_carbon`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    }
  );

  if (response.status !== 200) {
    return { toastError: "externalCampaign:error:generic" };
  }

  const {
    meta: { redirect_url: redirectUrl },
  } = (await response.json()) as {
    meta: { redirect_url: string };
  };

  return {
    ok: true,
    redirectUrl: redirectUrl,
  };
}

function ScopesExternalCampaign() {
  const { t } = useTranslation(["externalCampaign", "formatter"]);
  const { toast } = useToast();
  const navigation = useNavigation();
  const actionData = useActionData<typeof action>();

  const { externalCampaign } = useExternalCampaignContext();
  const { begesScopes, externalCampaignBegesScopesCarbon } =
    useLoaderData<typeof loader>();

  const {
    period_start: periodStart,
    period_end: periodEnd,
    review: { finished: reviewFinished },
  } = externalCampaign;
  const isLoading = navigation.state !== "idle";

  const [isInEdition, setIsInEdition] = useState(!reviewFinished);
  const [askForConfirm, setAskForConfirm] = useState(false);

  // https://reactrouter.com/api/hooks/useBlocker
  const blocker = useBlocker(askForConfirm);

  useEffect(() => {
    if (navigation.state === "idle") {
      // Show generic error message in negative toast
      if (actionData?.toastError) {
        toast({
          title: t(actionData.toastError),
          intent: "negative",
        });
      } else if (actionData?.ok) {
        const { redirectUrl } = actionData;

        // Redirect to redirectUrl
        window.location.assign(redirectUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData, navigation.state]);

  return (
    <PageWrapper>
      <div className={"mx-xl my-lg"}>
        <header className="mb-md flex justify-between">
          <div>
            <Typography step="3" as="h1" weight="bold" className="mb-3xs">
              {t("externalCampaign:startFootprint")}
            </Typography>
            <Typography step="0">
              {t("formatter:date", {
                value: new Date(periodStart),
              })}
              &nbsp;-&nbsp;
              {t("formatter:date", {
                value: new Date(periodEnd),
              })}
            </Typography>
          </div>
          <div>
            {reviewFinished && !isInEdition ? (
              <Button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  setIsInEdition(true);
                }}
              >
                {t("externalCampaign:scopes.updateFootprint")}
              </Button>
            ) : (
              <div className="gap-x-sm flex">
                {reviewFinished ? (
                  <Button
                    as="a"
                    href={`/tracking/external_campaigns/${externalCampaign.id}/edit`}
                    intent="primary"
                    shape="soft"
                  >
                    {t("externalCampaign:scopes.updateInformation")}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  form="begesScopesCarbonForm"
                  isLoading={isLoading}
                  onClick={() => setAskForConfirm(false)}
                >
                  {reviewFinished
                    ? t("externalCampaign:scopes.validateFootprintChanges")
                    : t("externalCampaign:scopes.integrateFootprint")}
                </Button>
              </div>
            )}
          </div>
        </header>
        <Form
          id="begesScopesCarbonForm"
          method="POST"
          onChange={() => {
            if (!askForConfirm) {
              setAskForConfirm(true);
            }
          }}
        >
          {begesScopes.map((scopes) => (
            <ScopesCard
              key={scopes.parent}
              scopes={scopes}
              externalCampaignBegesScopesCarbon={
                externalCampaignBegesScopesCarbon
              }
              disabled={!isInEdition}
            />
          ))}
          <ConfirmLeaveModal blocker={blocker} show={askForConfirm} />
        </Form>
      </div>
    </PageWrapper>
  );
}

export default ScopesExternalCampaign;
